import { h, Host } from "@stencil/core";
import { Label } from "../label/label";
import { debounce } from "../../utils/debounce";
let id = 0;
/**
 * @slot (default) - Label element slot, for label additional elements
 * @slot info-min - info label for the left side of the range
 * @slot info-max - info label for the right side of the range
 */
export class RangeInput {
    constructor() {
        this.min = 0;
        this.max = 10;
        this.value = this.min;
        this.step = 1;
        this.label = "";
        this.labelInfo = "";
        this.name = undefined;
        this.error = false;
        this.errorMsg = "Invalid value";
        this.disabled = false;
        this.inputId = "";
        this.hasSlottedLabel = false;
        this.markerOffsetX = 0;
    }
    baseClass = "ux-range-input";
    rangeInput;
    rangeMarker;
    element;
    /** range input event emitted on change, includes value, inputId and name */
    rangeInputEvent;
    componentWillLoad() {
        if (this.inputId === "") {
            this.inputId = `${this.baseClass}-id-${id++}`;
        }
        const firstLevelChildren = this.element.children;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < firstLevelChildren.length; i++) {
            const slotAttribute = firstLevelChildren[i].getAttribute("slot");
            if (slotAttribute === "info-min" || slotAttribute === "info-max") {
                return;
            }
            this.hasSlottedLabel = true;
        }
    }
    onResize() {
        this.recalculateMarkerPosition();
    }
    recalculateMarkerPosition = debounce(() => this.getMarkerPosition(), 100);
    componentDidLoad() {
        this.getMarkerPosition();
    }
    onChange = () => {
        this.error = false;
        this.rangeInputEvent.emit({
            value: this.rangeInput.value,
            inputId: this.inputId,
            name: this.name
        });
    };
    getMarkerPosition = () => {
        let offsetReference = (100 / this.max - this.min) * (parseFloat(this.rangeInput.value) - this.min);
        if (offsetReference < 0) {
            offsetReference = 0;
        }
        else if (offsetReference > 100) {
            offsetReference = 100;
        }
        this.markerOffsetX =
            (this.rangeInput.getBoundingClientRect().width - this.rangeMarker.clientWidth) * (offsetReference / 100);
    };
    onInvalid = () => {
        this.error = true;
    };
    render() {
        return (h(Host, { key: '58de519f96ae59d41f7800421469cf2a12677d0e', class: {
                [this.baseClass]: true,
                [`${this.baseClass}--disabled`]: this.disabled,
                [`${this.baseClass}--error`]: this.error
            } }, (this.label !== "" || this.hasSlottedLabel) && (h(Label, { key: '0c76d39487235865469659ebca812ecaa3cdd556', label: this.label, error: this.error, errorMsg: this.errorMsg, htmlFor: this.inputId, labelInfo: this.labelInfo }, h("slot", { key: 'f67d254b172d767d5e9f17832847566b436edcf2' }))), h("div", { key: '1e3ca5d7f322c0d40c2e8215d840f5bd7fa50119', class: `${this.baseClass}__track` }, h("input", { key: 'f1be125018911688721289bf1b2c4ff1e2d991f5', type: "range", name: this.name, min: this.min, max: this.max, step: this.step, id: this.inputId, value: this.value, onChange: this.onChange, onInput: this.getMarkerPosition, onInvalid: this.onInvalid,
            // @ts-expect-error Check for typing
            ref: (el) => (this.rangeInput = el), class: `${this.baseClass}__input`, "aria-invalid": this.error ? "true" : "false" }), h("div", { key: '0a712760ec666db547f8ac1df223cc15a42fd54a', class: `${this.baseClass}__marker`, style: {
                transform: `translateX(${this.markerOffsetX}px)`
            },
            // @ts-expect-error Check for typing
            ref: (el) => (this.rangeMarker = el) }, h("div", { key: '12a281a1d34773cc970f450d6dc79456a5b6a026', class: `${this.baseClass}__marker-line` }), h("div", { key: 'a4dc81217b2bca4c8367d4a40c589fde7c7854e5', class: `${this.baseClass}__marker-line` }), h("div", { key: '2e22f8df89651ecf6791645dc82763b962c32ee6', class: `${this.baseClass}__marker-line` }))), h("div", { key: '32e1204a3836ac6c658977c5560b5175ba2d77bd', class: `${this.baseClass}__info-container` }, h("div", { key: '5545734fedc31a9d57a1e01bc38a14f28814b1dd', class: `${this.baseClass}__info` }, h("slot", { key: '82c4fa242e36901d340a719371bdd7a30b8a829a', name: "info-min" })), h("div", { key: 'a5b930f21f5759d8a2a4677b0a85f2639be4645f', class: `${this.baseClass}__info` }, h("slot", { key: '84208f8744ee5ccdcde7158e7f9de594485ff2d6', name: "info-max" })))));
    }
    static get is() { return "ux-range-input"; }
    static get originalStyleUrls() {
        return {
            "$": ["range-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["range-input.css"]
        };
    }
    static get properties() {
        return {
            "min": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the minimum value that can be selected"
                },
                "attribute": "min",
                "reflect": false,
                "defaultValue": "0"
            },
            "max": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the maximum value that can be selected"
                },
                "attribute": "max",
                "reflect": false,
                "defaultValue": "10"
            },
            "value": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the default value to give to the imput, it defaults to the min value"
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "this.min"
            },
            "step": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | \"any\"",
                    "resolved": "\"any\" | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the step to give the range input element"
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "1"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the label of the component, can also be inserted per slot"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "labelInfo": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "additional info label for the the input field"
                },
                "attribute": "label-info",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the name to be assigned to the range input"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the component will be displayed in error mode"
                },
                "attribute": "error",
                "reflect": true,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the error message to be displayed to the user"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "\"Invalid value\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the input will be disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "inputId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "id attribute for the input element, this will be set automatically if it is not set by the developer"
                },
                "attribute": "input-id",
                "reflect": false,
                "defaultValue": "\"\""
            }
        };
    }
    static get states() {
        return {
            "hasSlottedLabel": {},
            "markerOffsetX": {}
        };
    }
    static get events() {
        return [{
                "method": "rangeInputEvent",
                "name": "uxRangeInputChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "range input event emitted on change, includes value, inputId and name"
                },
                "complexType": {
                    "original": "{\n        value: string;\n        inputId: string;\n        name: string;\n    }",
                    "resolved": "{ value: string; inputId: string; name: string; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "onResize",
                "target": "window",
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=range-input.js.map
